import {ijkToXyz, xyzToIjk} from "@/utils";

export const TYPE_PRODUCE = 4;
export const TYPE_INJECT = 5;
export const TYPE_CLOSED = 6;
export const TYPE_IMAGINARY = 7;
export const TYPE_AQUIFER = 8;

export const STATE_OPEN = 'OPEN';
export const STATE_SHUT = 'SHUT';
export const STATE_AUTO = 'AUTO';

function roundToDigits(num, digits = 2) {
    let mul = Math.pow(10, digits);
    num = Math.round(num * mul);
    return num / mul;
}

export class NodeData {
    constructor(fields, grid=null) {
        this.grid = grid || null;

        this._depth = fields?.depth ? parseFloat(fields.depth) : 0;
        this._bottomhole_depth = fields?.bottomhole_depth ? parseFloat(fields.bottomhole_depth) : 0;
        this._thickness = fields.h_well_net_thickness ? parseFloat(fields.h_well_net_thickness) : 0;
        if (!this._thickness && fields.h_net_thickness) {
            this._thickness = fields.h_net_thickness;
        }

        for (let propName of ['x_coord', 'y_coord', 'z_coord_relative', 'sw_water_saturation', 'p_pressure', 'q_initial_rate']) {
            let prop = fields[propName] || fields['_'+propName] || null;
            if (prop) {
                this['_'+propName] = parseFloat(prop);
            }
            else {
                this['_'+propName] = propName === 'q_initial_rate' ? null : 0;
            }

            this['_prev_'+propName] = null;
        }

        if (fields.z_coord && this._z_coord_relative === 0) {
            let depth = this.depth || this.bottomhole_depth || 0;
            if (depth === 0) {
                this._depth = parseFloat(fields.z_coord);
            }
            else {
                this._z_coord_relative = parseFloat(fields.z_coord) - depth;
                if (this._z_coord_relative < 0) {
                    this._z_coord_relative = parseFloat(fields.z_coord);
                }
            }
        }

        this.k_permeability = fields.k_permeability ? parseFloat(fields.k_permeability) : 0;
        if (!this.k_permeability && fields.k_well_permeability) {
            this.k_permeability = fields.k_well_permeability;
        }
        this.phi_porosity = fields?.phi_porosity ? parseFloat(fields?.phi_porosity) : 0;
        if (typeof (fields['l_perforation_length']) !== 'undefined') {
            this.l_perforation_length = parseFloat(fields.l_perforation_length);
        }

        this._skipProps = ['constructor', 'json', 'grid', 'maxK', 'boxSize', 'well', 'clone', 'afterP', 'afterQ', 'afterShift', 'afterSw'];
        this._enumerableGetters = [
            'x_coord', 'y_coord', 'z_coord', 'z_coord_relative', 'ijk', 'depth', 'bottomhole_depth',
            'sw_water_saturation', 'p_pressure', 'q_initial_rate'
        ];

        for(const property of this._enumerableGetters) {
            const descriptor = Object.getOwnPropertyDescriptor(NodeData.prototype, property);
            const modified_descriptor = Object.assign(descriptor, {enumerable: true});
            Object.defineProperty(this, property, modified_descriptor);
        }
    }

    get depth() {
        return this._depth;
    }

    set depth(value) {
        this._depth = parseFloat(value);
    }

    get bottomhole_depth() {
        if (this._bottomhole_depth) {
            return this._bottomhole_depth;
        }

        if (this.well) {
            return this.well.bottomhole_depth;
        }

        return 0;
    }

    set bottomhole_depth(value) {
        this._bottomhole_depth = parseFloat(value);
    }

    get x_coord() {
        return this._x_coord;
    }

    set x_coord(value) {
        this._prev_x_coord = this._x_coord;
        this._x_coord = parseFloat(value) || 0;
        if (this.afterShift) {
            this.afterShift();
        }
    }

    get y_coord() {
        return this._y_coord;
    }

    set y_coord(value) {
        this._prev_y_coord = this._y_coord;
        this._y_coord = parseFloat(value) || 0;
        if (this.afterShift) {
            this.afterShift();
        }
    }

    get z_coord_relative() {
        return this._z_coord_relative;
    }

    set z_coord_relative(value) {
        this._prev_z_coord_relative = this._z_coord_relative;
        this._z_coord_relative = parseFloat(value) || 0;
        if (this.afterShift) {
            this.afterShift();
        }
    }

    get z_coord() {
        let depth = this.grid?.top_depth || this.depth || 0;
        return this._z_coord_relative + depth;
    }

    set z_coord(value) {
        value = parseFloat(value);
        let depth = this.grid?.top_depth || this.depth || 0;
        this._prev_z_coord_relative = this._z_coord_relative;
        this._z_coord_relative = value - depth > 0
            ? value - depth
            : value;

        if (this.afterShift) {
            this.afterShift();
        }
    }

    get sw_water_saturation() {
        return this._sw_water_saturation;
    }

    set sw_water_saturation(value) {
        this._prev_sw_water_saturation = this._sw_water_saturation;
        this._sw_water_saturation = parseFloat(value) || 0;
        if (this.afterSw) {
            this.afterSw();
        }
    }

    get p_pressure() {
        return this._p_pressure;
    }

    set p_pressure(value) {
        this._prev_p_pressure = this._p_pressure;
        this._p_pressure = parseFloat(value) || 0;
        if (this.afterP) {
            this.afterP();
        }
    }

    get q_initial_rate() {
        return this._q_initial_rate;
    }

    set q_initial_rate(value) {
        this._prev_q_initial_rate = this._q_initial_rate;
        this._q_initial_rate = parseFloat(value) || 0;
        if (this.afterQ) {
            this.afterQ();
        }
    }

    get boxSize() {
        return this.grid?.cell_size
            ? this.grid?.cell_size || null
            : null;
    }

    get ijk() {
        if (!this.boxSize) {
            return null;
        }

        return xyzToIjk(this._x_coord, this._y_coord, this._z_coord_relative, this.boxSize);
    }

    set ijk(value) {
        if (!this.boxSize) {
            return;
        }

        if (!(value instanceof Array)) {
            return;
        }

        if (value.length !== 3) {
            return;
        }

        let [i, j, k] = value.map(coord => parseInt(coord));
        let [x, y, z] = ijkToXyz(i, j, k, this.boxSize);
        this.x_coord = x;
        this.y_coord = y;
        this.z_coord_relative = z;
    }

    json() {
        let baseProps = Object.keys(this);
        let getterProps = [];
        let proto = this.__proto__;
        let skipProps = this._skipProps;
        while (proto) {
            if (proto._skipProps) {
                skipProps = skipProps.concat(proto._skipProps);
            }
            let extraGetters = Object.keys(Object.getOwnPropertyDescriptors(proto));
            getterProps = getterProps.concat(extraGetters);
            proto = proto.__proto__;
        }

        let baseObjectProps = Object.keys(Object.getOwnPropertyDescriptors(({}).__proto__));
        let propNames = baseProps.concat(getterProps);
        skipProps = skipProps.concat(baseObjectProps);

        let resolved = {};
        for (let prop of propNames) {
            if (prop[0] === '_') {
                continue;
            }

            if (skipProps.includes(prop)) {
                continue;
            }

            resolved[prop] = this[prop];
        }

        return resolved;
    }

    clone() {
        return new NodeData(this.json(), this.grid);
    }
}

export class Node extends NodeData {
    constructor(fields, well = null, grid = null) {
        super(fields, grid);

        this.well = well;
        this.radius = fields?.radius ? parseFloat(fields?.radius) : 0;
        this.skin_factor = fields?.skin_factor ? parseFloat(fields?.skin_factor) : 0;
        this.state = fields?.state || STATE_OPEN;
        this._index = typeof (fields?.index) !== 'undefined' ? fields.index : null;

        this._enumerableGetters = ['perforated', 'well_id', 'h_net_thickness'];

        for(const property of this._enumerableGetters) {
            const descriptor = Object.getOwnPropertyDescriptor(Node.prototype, property);
            const modified_descriptor = Object.assign(descriptor, {enumerable: true});
            Object.defineProperty(this, property, modified_descriptor);
        }
    }

    get index() {
        if (!this.well) {
            return this._index;
        }

        let foundIndex = this.well.nodes.indexOf(this);
        if (foundIndex === -1) {
            foundIndex = this.well.nodes.findIndex(node => node.x_coord === this.x_coord && node.y_coord === this.y_coord && node.z_coord === this.z_coord)
        }

        return foundIndex;
    }

    get depth() {
        if (!this._depth && this.well && this.well.depth) {
            return this.well.depth;
        }

        return this._depth;
    }

    get bottomhole_depth() {
        if (!this._bottomhole_depth && this.well && this.well.bottomhole_depth) {
            return this.well.bottomhole_depth;
        }

        return this._bottomhole_depth;
    }

    get perforated() {
        return this.state === STATE_OPEN;
    }

    set perforated(value) {
        if (value) {
            this.state = STATE_OPEN;
        }
        else {
            this.state = STATE_SHUT;
        }
    }

    get well_id() {
        return this.well?.id || null;
    }

    get h_net_thickness() {
        return this._thickness;
    }

    set h_net_thickness(value) {
        this._thickness = parseFloat(value);
    }

    get sw_water_saturation() {
        if (!this._sw_water_saturation && this.well) {
            return this.well.sw_water_saturation;
        }

        return this._sw_water_saturation;
    }

    set sw_water_saturation(value) {
        this._prev_sw_water_saturation = this._sw_water_saturation;
        this._sw_water_saturation = parseFloat(value) || 0;
        if (this.afterSw) {
            this.afterSw();
        }
    }

    clone() {
        return new Node(this.json(), this.well, this.grid);
    }
}

export class Well extends NodeData {
    constructor(fields, grid=null, constants=null) {
        super(fields, grid);

        this.id = fields?.id || fields?.name || null;
        this.type = fields?.type ? parseInt(fields.type) : null;
        this._prevType = this.type;
        this.phase = fields?.phase || null;
        this._constants = constants || null;
        if (fields?.phi_well_porosity) {
            this.phi_porosity = fields?.phi_well_porosity ? parseFloat(fields?.phi_well_porosity) : 0;
        }
        else if (fields?.phi_porosity) {
            this.phi_porosity = fields?.phi_porosity ? parseFloat(fields?.phi_porosity) : 0;
        }

        let nodes = fields?.nodes || fields?._nodes || [];
        this._nodes = nodes.map(nodeData => new Node(nodeData, this, grid)) || [];

        this._skipProps = this._skipProps.concat(['addNode', 'shiftWell', 'recalcPressure', 'recalcPressureHydrostatic', 'h_net_thickness', 'k_permeability']);
        this._enumerableGetters = ['name', 'cell_ij', 'imaginary', 'nodes', 'path', 'xyz_path', 'phi_well_porosity', 'h_well_net_thickness', 'k_well_permeability'];

        for(const property of this._enumerableGetters) {
            const descriptor = Object.getOwnPropertyDescriptor(Well.prototype, property);
            const modified_descriptor = Object.assign(descriptor, {enumerable: true});
            Object.defineProperty(this, property, modified_descriptor);
        }

        this.recalcPressure();
    }

    get name() {
        return this.id;
    }

    set name(value) {
        this.id = value;
    }

    get imaginary() {
        return this.type === TYPE_IMAGINARY;
    }

    set imaginary(value) {
        if (value) {
            this._prevType = this.type;
            this.type = TYPE_IMAGINARY;
        }
        else {
            this.type = this._prevType;
        }
    }

    get phi_well_porosity() {
        return this.phi_porosity;
    }

    set phi_well_porosity(value) {
        this.phi_porosity = parseFloat(value);
    }

    get h_well_net_thickness() {
        return this._thickness;
    }

    set h_well_net_thickness(value) {
        this._thickness = parseFloat(value);
    }

    get k_well_permeability() {
        return this.k_permeability;
    }

    set k_well_permeability(value) {
        this.k_permeability = parseFloat(value);
    }

    get cell_ij() {
        if (this.ijk instanceof Array) {
            return [this.ijk[0], this.ijk[1]];
        }

        return null;
    }

    set cell_ij(value) {
        if (value instanceof Array && value.length === 2) {
            let newIjk = [value[0], value[1], this.ijk[2]];
            this.ijk = newIjk;
        }
    }

    get ijk() {
        if (!this.boxSize) {
            return null;
        }

        return xyzToIjk(this._x_coord, this._y_coord, 0, this.boxSize);
    }

    get nodes() {
        return this._nodes;
    }

    set nodes(value) {
        if (!(value instanceof Array)) {
            this._nodes = [];
            return;
        }

        this._nodes = value.map(nodeData => new Node(nodeData, this, this.grid));
    }

    addNode(nodeData) {
        this._nodes.push(new Node(nodeData, this, this.grid));
    }

    get path() {
        if (!this.cell_ij) {
            return [];
        }

        return [[this.cell_ij[0], this.cell_ij[1], 0]].concat(this._nodes
            .map(node => node.ijk)
            .filter(ijk => Boolean(ijk)));
    }

    set path(value) {
        return;
    }

    get xyz_path() {
        return this.path
            .map(ijk => {
                let [i, j, k] = ijk;
                return this.boxSize
                    ? ijkToXyz(i, j, k, this.boxSize)
                    : null;
            })
            .filter(xyz => Boolean(xyz));
    }

    set xyz_path(value) {
        return;
    }

    afterShift() {
        let dx = this._prev_x_coord !== null ? this._x_coord - this._prev_x_coord : 0;
        let dy = this._prev_y_coord !== null ? this._y_coord - this._prev_y_coord : 0;
        let dz = this._prev_z_coord_relative !== null ? this._z_coord_relative - this._prev_z_coord_relative : 0;

        this.shiftWell(dx, dy, dz);
        this._prev_x_coord = null;
        this._prev_y_coord = null;
        this._prev_z_coord_relative = null;
    }

    afterSw() {
        this.recalcPressure();
        this._prev_sw_water_saturation = null;
    }

    afterP() {
        this.recalcPressure();
        this._prev_p_pressure = null;
    }

    afterQ() {
        if (this.q_initial_rate > 0) {
            this.type = TYPE_INJECT;
        }
        else if (this.q_initial_rate < 0) {
            this.type = TYPE_PRODUCE;
        }
        else {
            this.type = TYPE_CLOSED;
        }

        this.recalcPressure();
        this._prev_q_initial_rate = null;
    }

    shiftWell(dx, dy, dz) {
        for (let node of this._nodes) {
            node.x_coord = node.x_coord + dx;
            node.y_coord = node.y_coord + dy;
            node.z_coord_relative = node.z_coord_relative + dz;
        }
    }

    recalcPressure() {
        let datum_depth = parseFloat(this._constants['dd'] || 0);
        let bhp_depth = this.bottomhole_depth || this.depth || datum_depth || 0;

        if (!(bhp_depth > 0)) {
            return;
        }

        if (!this._constants) {
            return;
        }

        let a = parseFloat(this._constants['a'] || 0);
        let nw = parseFloat(this._constants['nw'] || 0);
        let no = parseFloat(this._constants['no'] || 0);
        let p0 = parseFloat(this._constants['p'] || 0); // бар
        let mu_w = parseFloat(this._constants['mu_w'] || 0); //мПа/сек
        let mu_o = parseFloat(this._constants['mu_o'] || 0); //мПа/сек

        let rho_o = parseFloat(this._constants['rho_o'] || this._constants['rho_o_sc'] || 0); //кг/м3
        let rho_w = parseFloat(this._constants['rho_w'] || this._constants['rho_w_sc'] || 0); //кг/м3

        let siw = parseFloat(this._constants['sw'] || 0);
        let sor = parseFloat(this._constants['so'] || 0);
        let sw_default = this.type === TYPE_PRODUCE ? siw : 1-sor;
        let sw = this.sw_water_saturation || sw_default || 0;

        let anyMainZero = p0 * rho_o * rho_w === 0;
        let anyOtherZero = a * nw * no * mu_w * mu_o * sw === 0;
        if (anyMainZero) {
            return;
        }

        if (anyOtherZero) {
            return this.recalcPressureHydrostatic();
        }

        let g = 9.8; //м/сек2
        let mu_wave = mu_w / mu_o;
        let gamma_o = rho_o * g; //кг/(м2 * сек2)
        let gamma_w = rho_w * g; //кг/(м2 * сек2)
        let krw = sw => a * Math.pow(sw, nw);
        let kro = sw => Math.pow(sw, no);
        let fw = sw => krw(sw) / (krw(sw) + mu_wave * kro(sw));
        let qo = sw => (1-fw(sw)); //м3/день
        let qw = sw => fw(sw); //м3/день
        let dp = (sw, dh) => (gamma_w * qw(sw) + gamma_o * qo(sw))/(qw(sw) + qo(sw)) * dh; //кг/(м * сек2) = Па

        let dh = this.z_coord - bhp_depth;
        if (dh < 0) {
            return;
        }

        let dpBar = dp(sw, dh) / 1e5;
        if (!this.p_pressure) {
            this._p_pressure = roundToDigits(p0 + dpBar, 4);
        }

        for (let node of this._nodes) {
            dh = node.z_coord - bhp_depth;
            sw = node.sw_water_saturation || this.sw_water_saturation || sw_default || 0;
            dpBar = dp(sw, dh) / 1e5;

            node.p_pressure = roundToDigits(this.p_pressure + dpBar, 4);
        }
    }

    recalcPressureHydrostatic() {
        let datum_depth = parseFloat(this._constants['dd'] || 0);
        let bhp_depth = this.bottomhole_depth || this.depth || datum_depth || 0;

        if (!(bhp_depth > 0)) {
            return;
        }

        let p0 = parseFloat(this._constants['p'] || 0); // бар
        let rho_o = parseFloat(this._constants['rho_o'] || this._constants['rho_o_sc'] || 0); //кг/м3
        let rho_w = parseFloat(this._constants['rho_w'] || this._constants['rho_w_sc'] || 0); //кг/м3

        let siw = parseFloat(this._constants['sw'] || 0);
        let sor = parseFloat(this._constants['so'] || 0);
        let sw_default = this.type === TYPE_PRODUCE ? siw : 1-sor;
        let sw = this.sw_water_saturation || sw_default || 0;

        let rho = sw * rho_w + (1 - sw) * rho_o;
        let g = 9.8;

        let p = dh => p0 + (rho * g * dh) / 1e5;

        let dh = this.z_coord - bhp_depth;
        if (!this.p_pressure) {
            this._p_pressure = roundToDigits(p(dh), 4);
        }

        p0 = this.p_pressure;
        for (let node of this._nodes) {
            dh = node.z_coord - bhp_depth;
            sw = node.sw_water_saturation || this.sw_water_saturation || sw_default || 0;
            node.p_pressure = roundToDigits(p(dh), 4);
        }
    }

    clone() {
        return new Well(this.json(), this.grid, this._constants);
    }

    json() {
        let result = super.json();
        result.nodes = this.nodes.map(node => node.json());
        if (result.phi_porosity) {
            delete result.phi_porosity;
        }
        return result;
    }
}
